import React, { useEffect, useState } from 'react'
import { CopyIcon } from '../settings/icons'
import styles from './ErrorScreen.module.css'

export interface ErrorScreenActionProps {
  label: string
  onClick: () => void
  icon?: React.ReactNode
  variant?: 'minor' | 'primary'
}

export interface ErrorScreenImageProps {
  src: string
  left: number
  top: number
  width: number
  height: number
}

export interface ErrorScreenProps {
  image: ErrorScreenImageProps
  title: string
  description: string
  code?: string
  actions?: ErrorScreenActionProps[]
}

const CodeBadge = ({ code }: { code: string }) => {
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (!copied) return
    const timer = setTimeout(() => { setCopied(false) }, 1500)
    return () => { clearTimeout(timer) }
  }, [copied])

  return (
    <button
      type='button'
      className={styles.badge}
      title='Скопировать код ошибки'
      onClick={() => {
        // clipboard is unavailable over plain http, so never let it throw here
        void navigator.clipboard?.writeText(code).then(() => { setCopied(true) }, () => {})
      }}
    >
      <span className={styles.badgeText}>{copied ? 'Скопировано' : code}</span>
      <span className={styles.badgeIcon}><CopyIcon size={16} /></span>
    </button>
  )
}

export const ErrorScreen = ({ image, title, description, code, actions = [] }: ErrorScreenProps) => (
  <div className={styles.screen} role='alertdialog' aria-modal='true' aria-label={title}>
    <div className={styles.card}>
      <div className={styles.head}>
        <div className={styles.illustration}>
          <div className={styles.illustrationStage}>
            <img
              className={styles.illustrationImage}
              src={image.src}
              alt=''
              style={{ left: image.left, top: image.top, width: image.width, height: image.height }}
            />
          </div>
        </div>
        <div className={styles.text}>
          <h1 className={styles.title}>{title}</h1>
          <p className={styles.description}>{description}</p>
          {code && <CodeBadge code={code} />}
        </div>
      </div>
      {actions.length > 0 && (
        <div className={styles.actions}>
          {actions.map(({ label, onClick, icon, variant = 'minor' }) => (
            <button
              key={label}
              type='button'
              className={`${styles.button} ${variant === 'primary' ? styles.buttonPrimary : ''}`}
              onClick={onClick}
            >
              {icon && <span className={styles.buttonIcon}>{icon}</span>}
              <span className={styles.buttonLabel}>{label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  </div>
)

export default ErrorScreen
